import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { 
  ArrowRight,
  Star,
  Users,
  Clock,
  Shield,
  Gem,
  Mountain,
  MapPin,
  Camera,
  Bed
} from "lucide-react";
import { siteInfo, museum, tours, accommodation, testimonials, faqs } from "../data/mock";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SEOHead from "../components/SEOHead";
import WeatherWidget from "../components/WeatherWidget";

const HomePage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Umoona Opal Mine & Museum",
    "description": "Underground opal mine tours and authentic Australian opal shopping in Coober Pedy. Experience the world's opal capital with guided mine tours and museum exhibits.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -29.0135,
      "longitude": 134.7530
    },
    "telephone": "+61886724588",
    "url": "https://umoonaopalmine.com.au",
    "openingHours": "Mo-Su 08:30-17:30",
    "priceRange": "$15-$50",
    "hasMap": "https://goo.gl/maps/example",
    "petsAllowed": false,
    "amenityFeature": [
      {
        "@type": "LocationFeatureSpecification",
        "name": "Pet Policy",
        "value": "Dogs and pets are NOT permitted at Umoona Opal Mine & Museum for safety reasons"
      }
    ]
  };

  return (
    <div>
      <SEOHead
        title="Underground Opal Mine Tours & Authentic Australian Opals"
        description="Visit Umoona Opal Mine & Museum in Coober Pedy for underground mine tours, authentic Australian opal shopping, and museum exhibits. Book your unique underground experience today!"
        keywords="opal mine tours, Coober Pedy tours, underground tours, Australian opals, opal shopping, mine museum, South Australia tourism, authentic opals, opal jewelry, underground experience"
        canonical="/"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={siteInfo.heroImage}
            alt="Umoona Opal Mine - Coober Pedy's Underground Wonder"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent"></div>
        </div>
        
        {/* Animated particles/dust effect */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-400/30 rounded-full animate-pulse"></div>
          <div className="absolute top-3/4 right-1/3 w-1 h-1 bg-yellow-300/40 rounded-full animate-ping"></div>
          <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-orange-300/20 rounded-full animate-pulse"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 z-10">
          <div className="text-center max-w-5xl mx-auto">
            <div className="space-y-12">
              
              {/* Pre-heading */}
              <div className="space-y-6">
                <Badge variant="secondary" className="bg-orange-500/20 text-orange-300 border border-orange-400/30 hover:bg-orange-500/30 px-6 py-3 text-lg backdrop-blur-sm animate-fade-in">
                  💎 Coober Pedy's Most Iconic Underground Experience
                </Badge>
                
                {/* Main heading with dramatic effect */}
                <div className="space-y-4">
                  <h1 className="text-5xl lg:text-8xl font-black text-white leading-none tracking-tight">
                    <span className="block animate-slide-up-1">EXPERIENCE THE</span>
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500 animate-slide-up-2">
                      UNDERGROUND
                    </span>
                    <span className="block text-4xl lg:text-6xl font-bold mt-4 animate-slide-up-3">
                      Umoona Opal Mine - Est. 1920s
                    </span>
                  </h1>
                </div>
                
                <p className="text-xl lg:text-2xl text-gray-200 leading-relaxed max-w-4xl mx-auto animate-fade-in-delayed">
                  Experience the <span className="text-orange-300 font-semibold">largest single underground tourist attraction</span> in Coober Pedy. 
                  Journey through authentic mine workings, explore our <span className="text-green-300 font-semibold">free museum</span>, 
                  and discover genuine Australian opals in the depths of the underground city.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center animate-fade-in-delayed-2">
                <Button 
                  asChild
                  size="lg" 
                  className="mining-cta font-bold px-10 py-5 text-xl rounded-xl shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  <Link to="/free-museum">
                    🏛️ Explore FREE Museum
                    <ArrowRight className="ml-3 w-6 h-6" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  variant="outline" 
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-10 py-5 text-xl rounded-xl backdrop-blur-sm shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  <Link to="/underground-tours">
                    ⛏️ Underground Tours
                  </Link>
                </Button>
              </div>

              {/* Impact Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-12 animate-fade-in-delayed-3 max-w-2xl mx-auto">
                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-5xl font-black text-green-400 mb-3">FREE</div>
                  <div className="text-white font-semibold text-lg">Museum Entry</div>
                  <div className="text-gray-300 text-sm">Completely Free Access</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="text-5xl font-black text-orange-400 mb-3">100+</div>
                  <div className="text-white font-semibold text-lg">Years Deep</div>
                  <div className="text-gray-300 text-sm">Since 1920s</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Weather Widget */}
      <WeatherWidget />

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Why Choose Umoona?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer the most authentic and comprehensive opal mine experience in Coober Pedy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4 p-0">
                <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Shield className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold">100% Authentic</h3>
                <p className="text-gray-600">
                  Experience genuine underground mining in original workings dating back to the 1920s
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4 p-0">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Gem className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold">Free Museum</h3>
                <p className="text-gray-600">
                  Explore our extensive underground museum at no cost - perfect for all budgets
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4 p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold">Expert Guides</h3>
                <p className="text-gray-600">
                  Learn from knowledgeable local guides with decades of mining experience
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <CardContent className="space-y-4 p-0">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold">Underground Tours</h3>
                <p className="text-gray-600">
                  Take a guided tour into the original Umoona Opal mining tunnels below Coober Pedy
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Complete Coober Pedy Experience
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for an unforgettable visit to Australia's opal capital
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8">
            
            {/* Free Museum */}
            <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
              <Link to="/free-museum">
                <div className="aspect-[4/3] relative overflow-hidden cursor-pointer">
                  <img 
                    src={museum.images[0]}
                    alt="Underground Museum at Umoona"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-green-600 hover:bg-green-700">
                    FREE ENTRY
                  </Badge>
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="font-semibold">Since 1920s</p>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="bg-white/90 text-gray-900 px-4 py-2 rounded-full font-semibold">
                      Click to Explore →
                    </div>
                  </div>
                </div>
              </Link>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-bold">Underground Museum</h3>
                <p className="text-gray-600">
                  Explore mining history, Aboriginal culture, and opal formation in our extensive free museum.
                </p>
                <Button asChild className="w-full mining-cta">
                  <Link to="/free-museum">Explore Museum</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Underground Tours */}
            <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
              <Link to="/underground-tours">
                <div className="aspect-[4/3] relative overflow-hidden cursor-pointer">
                  <img 
                    src={tours.images[1]}
                    alt="Underground Mine Tours"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-orange-600 hover:bg-orange-700">
                    FROM $15
                  </Badge>
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="font-semibold">Daily Tours</p>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="bg-white/90 text-gray-900 px-4 py-2 rounded-full font-semibold">
                      Click to Book Tour →
                    </div>
                  </div>
                </div>
              </Link>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-bold">Guided Mine Tours</h3>
                <p className="text-gray-600">
                  Experience authentic underground mining with expert guides in original mine workings.
                </p>
                <Button asChild className="w-full mining-cta">
                  <Link to="/underground-tours">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Group Accommodation */}
            <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
              <Link to="/group-accommodation">
                <div className="aspect-[4/3] relative overflow-hidden cursor-pointer">
                  <img 
                    src="/bunk1.jpg"
                    alt="Underground Accommodation"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-emerald-600 hover:bg-emerald-700">
                    24°C CONSTANT
                  </Badge>
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="font-semibold">102 Beds Available</p>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="bg-white/90 text-gray-900 px-4 py-2 rounded-full font-semibold">
                      Click to Learn More →
                    </div>
                  </div>
                </div>
              </Link>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-bold">Underground Accommodation</h3>
                <p className="text-gray-600">
                  For Schools and Tour Groups - our underground bunkhouses can accommodate in a unique Coober Pedy experience
                </p>
                <Button asChild variant="outline" className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                  <Link to="/group-accommodation">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Live Webcam */}
            <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
              <Link to="/webcam">
                <div className="aspect-[4/3] relative overflow-hidden cursor-pointer">
                  <img 
                    src="/coober-pedy-webcam-live.jpg"
                    alt="Live Coober Pedy Webcam - Current View"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-red-600 hover:bg-red-700 animate-pulse">
                    🔴 LIVE NOW
                  </Badge>
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="font-semibold">Current Coober Pedy View</p>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="bg-white/90 text-gray-900 px-4 py-2 rounded-full font-semibold">
                      Click to Watch Live →
                    </div>
                  </div>
                </div>
              </Link>
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-bold">Live Coober Pedy Webcam</h3>
                <p className="text-gray-600">
                  Watch live views of Coober Pedy's main street and unique desert landscape from our elevated webcam.
                </p>
                <Button asChild variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
                  <Link to="/webcam">Watch Live</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              What Our Visitors Say
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of satisfied visitors who've experienced the magic of Umoona
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-6">
                <CardContent className="space-y-4 p-0">
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {testimonial.tour}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know for your visit
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg border shadow-sm">
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                Ready for Your Underground Adventure?
              </h2>
              <p className="text-xl text-amber-100 max-w-2xl mx-auto">
                Book your underground tour today and discover why Umoona is Coober Pedy's 
                most beloved attraction. Free museum access included!
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-amber-600 hover:bg-amber-50 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/underground-tours">
                  Book Underground Tour
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;